import React from "react";
import {
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import TuneIcon from "@mui/icons-material/Tune";

import format from "date-fns/format";
import addDays from "date-fns/addDays";
import addMonths from "date-fns/addMonths";
import endOfWeek from "date-fns/endOfWeek";
import startOfWeek from "date-fns/startOfWeek";

import TrainingMeetingButton from "./training-meeting-button";

type SupportedCalendarView = "month" | "week" | "agenda";


interface CalendarToolbarProps {
  view: SupportedCalendarView;
  setView: (view: SupportedCalendarView) => void;
  date: Date;
  setDate: (date: Date) => void;
  addButtonClick: () => void;
  isSidebarOpen: boolean;
  showSidebarWarning?: boolean;
  onToggleSidebar: () => void;
}

const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  view,
  setView,
  date,
  setDate,
  addButtonClick,
  isSidebarOpen,
  showSidebarWarning = false,
  onToggleSidebar,
}) => {
  const getDateLabel = (date: Date, view: SupportedCalendarView) => {
    if (view == "month") {
      return format(date, "MMMM yyyy");
    }

    if (view == "week") {
      const weekStart = startOfWeek(date, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 0 });

      const firstVisibleDay = weekStart;
      const lastVisibleDay = weekEnd;

      const startMonth = format(firstVisibleDay, "MMMM");
      const endMonth = format(lastVisibleDay, "MMMM");
      const startYear = format(firstVisibleDay, "yyyy");
      const endYear = format(lastVisibleDay, "yyyy");

      if (startMonth !== endMonth || startYear !== endYear) {
        if (startYear !== endYear) {
          return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
        }

        return `${startMonth} - ${endMonth} ${startYear}`;
      }

      return `${startMonth} ${startYear}`;
    }

    if (view == "agenda") {
      const thirtyDaysLaterDate = new Date(date);
      thirtyDaysLaterDate.setDate(thirtyDaysLaterDate.getDate() + 30);

      const startMonth = format(date, "MMMM");
      const endMonth = format(thirtyDaysLaterDate, "MMMM");
      const startYear = format(date, "yyyy");
      const endYear = format(thirtyDaysLaterDate, "yyyy");

      if (startMonth !== endMonth || startYear !== endYear) {
        if (startYear !== endYear) {
          return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
        }

        return `${startMonth} - ${endMonth} ${startYear}`;
      }

      return `${startMonth} ${startYear}`;
    }

    return "";
  };

  const dateLabel = getDateLabel(date, view);

  const setDateToNow = () => {
    setDate(new Date());
  };

  const setDateNext = () => {
    if (view == "month") {
      setDate(addMonths(date, 1));
    }

    if (view == "week") {
      setDate(addDays(date, 7));
    }

    if (view == "agenda") {
      setDate(addDays(date, 30));
    }
  };

  const setDatePrev = () => {
    if (view == "month") {
      setDate(addMonths(date, -1));
    }

    if (view == "week") {
      setDate(addDays(date, -7));
    }

    if (view == "agenda") {
      setDate(addDays(date, -30));
    }
  };

  const setDateById = (id: string) => {
    if (id == "next") {
      setDateNext();
    }

    if (id == "prev") {
      setDatePrev();
    }

    if (id == "today") {
      setDateToNow();
    }
  };

  const getNavigationTooltip = (id: string) => {
    if (id == "next") {
      return view === "week" ? "Next week" : "Next month";
    }

    if (id == "prev") {
      return view === "week" ? "Previous week" : "Previous month";
    }

    if (id == "today") {
      return "Go to today";
    }

    return "";
  };

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      width={"100%"}
      boxSizing={"border-box"}
      px={{ xs: 1, sm: 2 }}
      spacing={1}
    >
      <Stack
        direction={"row"}
        spacing={1}
        justifyContent={"space-between"}
        alignItems={"center"}
        width={"55%"}
      >
        <Stack direction={"row"} spacing={1} alignItems={"center"}>

          <Button
            variant="contained"
            onClick={addButtonClick}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: "1.5rem",
              px: 2,
              py: 1,
              height: "40px",
              fontWeight: 600,
            }}
          >
            Schedule Meeting
          </Button>

            <TrainingMeetingButton />
        </Stack>
        <Typography variant={"h4"}>{dateLabel}</Typography>
      </Stack>
      <Stack
        direction={"row"}
        spacing={2}
        alignItems={"center"}
        justifyItems={"center"}
        width={"fit-content"}
      >
        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
          <Tooltip title={getNavigationTooltip("prev")}>
            <IconButton
              onClick={() => setDateById("prev")}
              sx={{
                height: "30px",
                width: "30px",
                padding: 0,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={() => setDateById("today")}
            sx={{
              px: 2,
              py: 0.5,
              borderRadius: "1.5rem",
            }}
          >
            Today
          </Button>
          <Tooltip title={getNavigationTooltip("next")}>
            <IconButton
              onClick={() => setDateById("next")}
              sx={{
                height: "30px",
                width: "30px",
                padding: 0,
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider orientation="vertical" flexItem />
        <ButtonGroup
          variant="outlined"
          sx={{
            borderRadius: "1.5rem",
            "& .MuiButton-root": {
              color: "#555",
              borderColor: "#BBB",
              "&:not(:last-child)": {
                borderRight: "1px solid #BBB",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "#999",
              },
            },
            "& .MuiButton-root:first-of-type": {
              borderTopLeftRadius: "1.5rem",
              borderBottomLeftRadius: "1.5rem",
            },
            "& .MuiButton-root:last-of-type": {
              borderTopRightRadius: "1.5rem",
              borderBottomRightRadius: "1.5rem",
            },
          }}
        >
          <Tooltip title="Month view">
            <Button
              onClick={() => setView("month")}
              aria-pressed={view === "month"}
              sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
            >
              <CalendarViewMonthIcon
                sx={{
                  fontSize: 22,
                  color: view === "month" ? "#999" : "#555",
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip title="Week view">
            <Button
              onClick={() => setView("week")}
              aria-pressed={view === "week"}
              sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
            >
              <CalendarViewWeekIcon
                sx={{
                  fontSize: 22,
                  color: view === "week" ? "#999" : "#555",
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip title="Agenda view">
            <Button
              onClick={() => setView("agenda")}
              aria-pressed={view === "agenda"}
              sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
            >
              <CalendarViewDayIcon
                sx={{
                  fontSize: 22,
                  color: view === "agenda" ? "#999" : "#555",
                }}
              />
            </Button>
          </Tooltip>
        </ButtonGroup>
        <Divider orientation="vertical" flexItem />
        <Tooltip title={isSidebarOpen ? "Hide calendar sidebar" : "Show calendar sidebar"}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onToggleSidebar}
            aria-label="Toggle calendar sidebar"
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "1.5rem",
              px: 0,
              py: 0,
              height: "40px",
              minWidth: "40px",
              width: "40px",
              fontWeight: 600,
              backgroundColor: isSidebarOpen ? "rgba(34, 112, 181, 0.08)" : "transparent",
              "&::after": showSidebarWarning ? {
                content: '""',
                position: "absolute",
                top: 2,
                right: 2,
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: "warning.main",
                boxShadow: "0 0 0 2px #fff",
              } : {},
            }}
          >
            <TuneIcon />
          </Button>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default CalendarToolbar;
